/**
 * Sign in with Apple on a page (docs/ops-protocol.md, "Sign in with Apple"):
 * Apple's JS (`appleid.auth.js`) in a popup, for an app that configured its
 * Services ID and return URL with `configureSignInWithApple` (both registered
 * with Apple for the page's domain). `signIn` loads the script once, asks,
 * and shapes the answer as `ASAuthorizationAppleIDCredential` does: the
 * `user` is the identity token's subject, the name and email come only the
 * first time the viewer agrees. A page cannot ask Apple what a credential is
 * now, so `credentialState` is `unknown`. Natively the host answers instead
 * (Host/AppleSignInBridge.swift).
 */

import { base64Decode } from './base64';
import type { AppleCredentialState, AppleIDCredential, AppleSignInHost } from './hostRequests';

export interface AppleSignInConfiguration {
  /** The Services ID registered with Apple for the page's domain (`com.example.app.web`). */
  clientId: string;
  /** The return URL registered with it, on this domain (`https://example.com/app/`). */
  redirectURI: string;
  /** Apple's script, where a page serves its own copy. */
  scriptURL?: string;
}

/** What Apple's script puts on the page. */
interface AppleIDAuth {
  init(options: { clientId: string; scope: string; redirectURI: string; state?: string; nonce?: string; usePopup: boolean }): void;
  signIn(): Promise<AppleSignInResponse>;
}

interface AppleSignInResponse {
  authorization: { code: string; id_token: string; state?: string };
  user?: { email?: string; name?: { firstName?: string; lastName?: string } };
}

const SCRIPT_URL = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

let configuration: AppleSignInConfiguration | null = null;

/** Names the page's Services ID and return URL; before it, a sign-in fails `unsupported: …`. */
export function configureSignInWithApple(config: AppleSignInConfiguration | null): void {
  configuration = config;
}

export function signInWithAppleConfiguration(): AppleSignInConfiguration | null {
  return configuration;
}

/** The payload of a JWT, or null when it is not one. */
export function jwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed: unknown = JSON.parse(new TextDecoder().decode(base64Decode(padded)));
    return parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Apple's answer as the credential the app reads. */
export function credentialOf(response: AppleSignInResponse): AppleIDCredential {
  const token = response.authorization?.id_token ?? null;
  const claims = token ? jwtPayload(token) : null;
  const user = typeof claims?.['sub'] === 'string' ? (claims['sub'] as string) : '';
  const email = response.user?.email ?? (typeof claims?.['email'] === 'string' ? (claims['email'] as string) : null);
  const name = response.user?.name;
  return {
    user,
    email,
    fullName: name
      ? { givenName: name.firstName ?? null, familyName: name.lastName ?? null, middleName: null, namePrefix: null, nameSuffix: null, nickname: null }
      : null,
    identityToken: token,
    authorizationCode: response.authorization?.code ?? null,
    realUserStatus: 'unsupported',
    state: response.authorization?.state ?? null,
  };
}

let loading: Promise<AppleIDAuth> | null = null;

/** Apple's script, loaded once. */
function appleIDAuth(scriptURL: string): Promise<AppleIDAuth> {
  const existing = (globalThis as { AppleID?: { auth: AppleIDAuth } }).AppleID?.auth;
  if (existing) return Promise.resolve(existing);
  return (loading ??= new Promise<AppleIDAuth>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptURL;
    script.async = true;
    script.onload = () => {
      const auth = (globalThis as { AppleID?: { auth: AppleIDAuth } }).AppleID?.auth;
      if (auth) resolve(auth);
      else reject(new Error('appleSignIn: the script loaded without AppleID.auth'));
    };
    script.onerror = () => {
      loading = null;
      reject(new Error('network: could not load Sign in with Apple'));
    };
    document.head.append(script);
  }));
}

/** Whether Apple's script reported the viewer closing the popup or declining. */
function isCancellation(error: unknown): boolean {
  const code = (error as { error?: string } | null)?.error;
  return code === 'popup_closed_by_user' || code === 'user_cancelled_authorize' || code === 'popup_blocked_by_browser';
}

export function browserAppleSignIn(): AppleSignInHost {
  return {
    async signIn(options) {
      const config = configuration;
      if (!config) throw new Error('unsupported: Sign in with Apple is not configured here (configureSignInWithApple on a page)');
      const auth = await appleIDAuth(config.scriptURL ?? SCRIPT_URL);
      const scope = options.scopes.map((item) => (item === 'fullName' ? 'name' : 'email')).join(' ');
      auth.init({
        clientId: config.clientId,
        scope,
        redirectURI: config.redirectURI,
        ...(options.state ? { state: options.state } : {}),
        ...(options.nonce ? { nonce: options.nonce } : {}),
        usePopup: true,
      });
      let response: AppleSignInResponse;
      try {
        response = await auth.signIn();
      } catch (error) {
        if (isCancellation(error)) throw new Error('cancelled: the viewer closed Sign in with Apple');
        throw new Error(`appleSignIn: ${(error as { error?: string } | null)?.error ?? String(error)}`);
      }
      return credentialOf(response);
    },
    async credentialState(): Promise<AppleCredentialState> {
      return 'unknown';
    },
  };
}
